/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useGetLiveFormQuery,
  useSaveLiveFormMutation,
  useUpdateLiveFormMutation,
} from '@gen2/api/live-form/hooks';
import {
  HeaderTitle,
  StyledHeader,
  StyledHeaderActions,
  StyledViewer,
  StyledWebViewerBox,
} from '@gen2/app/components/invite-request/variant/live-form/peview-web-viewer-modal.styled';
import { useSendInviteStore } from '@gen2/app/invites/send-invites/store';
import { useAuth, useToast } from '@gen2/hooks';
import { TContact } from '@gen2/types/contact';
import { TUser } from '@gen2/types/user';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Dialog } from '@mui/material';
import NutrientViewer, {
  AnnotationsUnion,
  FormField,
  Instance,
  List,
} from '@nutrient-sdk/viewer';
import { customDecode } from '@nx-fe/components';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useUnmount } from 'usehooks-ts';
import { onDragStart } from './nutrient/sidePanel/helpers';
import SidePanel from './nutrient/sidePanel/sidePanel';
import {
  createDebouncedAnnotationHandlers,
  CustomInstantJSON,
  generateInstantJSON,
  handleAnnotationCreation,
  handleAnnotationDelete,
  handleFormFieldUpdate,
  handleFormFieldValueUpdate,
  replacePlaceholdersByContacts,
  TOOLBAR_ITEMS,
} from './nutrient/utils/common-helpers';
import getAnnotationRenderers from './nutrient/utils/customAnnotationRenderer';
import customizeUIForInitials from './nutrient/utils/customizeUIForInitials';
import handleDrop from './nutrient/utils/dropAnnotations';
import { userChange } from './nutrient/utils/helpers';
import { AnnotationTypeEnum, TAssignee } from './nutrient/utils/types';

const defaultAssignee = {
  id: '',
  first_name: '',
  last_name: '',
  email: '',
};

/**
 * Checks if the form designer content element corresponds to a DateTimeField annotation
 * by examining the PSPDFKit instance annotations
 */
const checkIfDateTimeFieldAnnotation = async (
  formDesignerContent: Element,
  instance: Instance | null,
): Promise<boolean> => {
  if (!instance) return false;

  try {
    // Get all annotations from all pages
    for (let pageIndex = 0; pageIndex < instance.totalPageCount; pageIndex++) {
      const annotations = await instance.getAnnotations(pageIndex);

      for (const annotation of annotations) {
        // Check if this annotation has customData with DateTimeField type
        if (
          annotation.customData &&
          annotation.customData['type'] === AnnotationTypeEnum.DateAndTimeField
        ) {
          // Check if this annotation's DOM element matches the formDesignerContent
          // We can do this by checking if the annotation's name or ID appears in the element
          const annotationName = annotation.name || annotation.id;
          if (
            annotationName &&
            formDesignerContent.textContent?.includes(annotationName)
          ) {
            return true;
          }
        }
      }
    }
  } catch (error) {
    console.error('Error checking DateTimeField annotation:', error);
    // The function will return false, allowing the default behavior to proceed
  }

  return false;
};

export interface FormViewerModalProps {
  fileId: string;
  fileName: string;
  onClose: () => void;
  open: boolean;
  fileUrl: string;
  contacts?: TContact[];
}

let renderCount = 0;
export const NutrientFormViewerModal = ({
  fileName,
  onClose,
  open,
  fileId,
  fileUrl,
  contacts = [],
}: FormViewerModalProps) => {
  const viewer = useRef(null);

  const { inviteId } = useParams<{
    inviteId: string;
  }>();

  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(true);

  const toast = useToast();
  const { user, featureFlags } = useAuth();
  const siStore = useSendInviteStore();
  const { mutate: saveMutate, isLoading: saveLoading } =
    useSaveLiveFormMutation();
  const { mutate: updateMutate, isLoading: updateLoading } =
    useUpdateLiveFormMutation();

  const [nutrientInstance, setNutrientInstance] = useState<Instance | null>(
    null,
  );

  // State to store the current signee i.e the user who is currently selected for which the field will be added
  const initialAssignee = contacts.length > 0 ? contacts[0] : defaultAssignee;
  const [currSignee, setCurrSignee] = useState(initialAssignee);
  const currSigneeRef = useRef(currSignee);
  currSigneeRef.current = currSignee;

  // Solution：solely to work around an issue that only appears in Development mode,
  // forcing WebViewer Initialization Only Occurs Once in Development build -- the Strict Mode
  const beenInitialised = useRef<boolean>(false);

  // State to store the current user i.e the user who is currently Loggedin
  const [currUser] = useState<TAssignee>(user as TAssignee);
  const currUserRef = useRef(currUser);
  currUserRef.current = currUser;

  const { data } = useGetLiveFormQuery(fileId);

  const [isSignatureTypeEnabled, setIsSignatureTypeEnabled] = useState(true);

  // State to store the current page index
  const [onPageIndex, setOnPageIndex] = useState<number>(0);
  const onPageIndexRef = useRef(onPageIndex);

  onPageIndexRef.current = onPageIndex;

  useEffect(() => {
    if (data?.liveform?.fields?.is_type_signature_enabled !== undefined) {
      setIsSignatureTypeEnabled(data.liveform.fields.is_type_signature_enabled);
    }
  }, [data?.liveform?.fields?.is_type_signature_enabled]);

  const instantJSON: CustomInstantJSON = useMemo(() => {
    let updatedAnnotations: any[] = [];
    const initialJsonData: CustomInstantJSON = {
      format: 'https://pspdfkit.com/instant-json/v1',
      customFromAPI: true,
    };

    if (data !== undefined) {
      const responseData = data?.liveform?.fields?.json_data as any;
      const type = data?.liveform?.fields?.type as string;
      updatedAnnotations = replacePlaceholdersByContacts(
        responseData?.annotations,
        contacts,
        type,
      );

      if (responseData) {
        return {
          ...initialJsonData,
          ...responseData,
          annotations: updatedAnnotations,
        };
      }

      return initialJsonData;
    }

    return {
      ...initialJsonData,
      customFromAPI: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, data]);

  const liveFormFieldId = useMemo(() => {
    if (data?.liveform?.fields?.id) {
      return data?.liveform?.fields?.id;
    }

    return '';
  }, [data?.liveform?.fields?.id]);

  // For Custom signature / initial field appearance
  const mySignatureIdsRef = useRef([]);
  const [signatureAnnotationIds, setSignatureAnnotationIds] = useState<
    string[]
  >([]);

  // For Custom Add Signature / Intitial field appearance
  const [sessionSignatures, setSessionSignatures] = useState<any>([]);
  const [sessionInitials, setSessionInitials] = useState<any>([]);

  // Hack: Store the last duplicated radio button bounding box for manual injection
  const [lastDuplicatedRadioButtonBbox, setLastDuplicatedRadioButtonBbox] =
    useState<{
      annotationId: string;
      bbox: number[];
    } | null>(null);

  function onDragEnd(event: React.DragEvent<HTMLDivElement>) {
    (event.target as HTMLDivElement).style.opacity = '1';
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const [isTextAnnotationMovable, setIsTextAnnotationMovable] = useState(false);
  const isTextAnnotationMovableRef = useRef(isTextAnnotationMovable);
  isTextAnnotationMovableRef.current = isTextAnnotationMovable;

  // Tracking whether add Signature/Initial UI
  let isCreateInitial = false;
  const trackInst = useRef<any>(null);
  const loadPSPDFKit = useCallback(
    async (instantJSON: CustomInstantJSON, el?: HTMLElement) => {
      if (NutrientViewer && fileUrl && el && !beenInitialised.current) {
        NutrientViewer.unload(el);
        beenInitialised.current = true;
        try {
          const {
            UI: { createBlock, Recipes, Interfaces },
          } = NutrientViewer;

          const instance = await NutrientViewer.load({
            theme: NutrientViewer.Theme.LIGHT,
            container: el,
            document: fileUrl,
            instantJSON: instantJSON,
            licenseKey: customDecode(window.$xr9c42),
            baseUrl: `${window.location.protocol}//${window.location.host}/assets/lib/pspdfkit/`,
            disableTextSelection: false,
            autoSaveMode: NutrientViewer.AutoSaveMode.IMMEDIATE,
            toolbarItems: [...TOOLBAR_ITEMS].filter(
              (item) => !['export-pdf', 'print'].includes(item.type),
            ),
            initialViewState: new NutrientViewer.ViewState({
              interactionMode: NutrientViewer.InteractionMode.FORM_CREATOR,
            }),
            styleSheets: [`/assets/lib/pspdfkit/viewer.css`],
            isEditableAnnotation: (annotation) => !annotation.isSignature,
            ui: {
              [Interfaces.CreateSignature]: ({ props }: { props: any }) => ({
                content: createBlock(
                  Recipes.CreateSignature,
                  props,
                  ({ ui }: { ui: any }) => {
                    if (isCreateInitial) ui = customizeUIForInitials(ui);
                    return ui.createComponent();
                  },
                ).createComponent(),
              }),
            },
            customRenderers: {
              Annotation: ({ annotation }) => {
                return getAnnotationRenderers({
                  annotation,
                  instance: trackInst.current,
                });
              },
            },
          });

          setLoading(false);
          setNutrientInstance(instance);
        } catch (error) {
          console.error('Error loading PSPDFKit:', error);
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setTimeout(() => loadPSPDFKit(instantJSON, viewer.current!));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const configureInstanceEvents = useCallback(
    (inst: Instance) => {
      inst.setViewState((viewState) =>
        viewState.set(
          'showSignatureValidationStatus',
          NutrientViewer.ShowSignatureValidationStatusMode.IF_SIGNED,
        ),
      );

      inst.addEventListener('viewState.currentPageIndex.change', (page) =>
        setOnPageIndex(page),
      );

      //@ts-ignore
      const cont = inst.contentDocument.host;
      cont.ondrop = async (e: any) =>
        await handleDrop(
          e,
          inst,
          NutrientViewer,
          currSigneeRef,
          currUserRef,
          onPageIndexRef,
        );

      const { handleDuplicateButton, handleRemoveDateTimeField, cleanup } =
        createDebouncedAnnotationHandlers(
          inst,
          featureFlags,
          storeDuplicatedRadioButtonBbox,
        );

      debouncedHandlersCleanupRef.current = cleanup;

      inst.addEventListener(
        'annotations.press',
        (event: { preventDefault?: any; annotation?: any }) => {
          if (!viewer.current) {
            return;
          }
          const { annotation } = event;
          if (annotation.customData?.isInitial) {
            setSessionInitials((prev: any) => [...prev, annotation]);
            isCreateInitial = true;
          } else {
            setSessionSignatures((prev: any) => [...prev, annotation]);
            isCreateInitial = false;
          }

          if (
            !isTextAnnotationMovableRef.current &&
            annotation instanceof NutrientViewer.Annotations.TextAnnotation
          ) {
            event.preventDefault();
          }

          handleDuplicateButton(annotation);
          handleRemoveDateTimeField(annotation);

          if (
            !isTextAnnotationMovableRef.current &&
            event.annotation instanceof
              NutrientViewer.Annotations.TextAnnotation
          ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            event.preventDefault();
          }
        },
      );

      inst.addEventListener('annotations.transform', async (event) => {
        const { annotation }: { annotation?: any } = event;
        handleDuplicateButton(annotation);
      });

      inst.addEventListener(
        'storedSignatures.create',
        async (annotation: any) => {
          if (isCreateInitial) {
            setSessionInitials([...sessionInitials, annotation]);
          } else {
            setSessionSignatures([...sessionSignatures, annotation]);
          }
        },
      );

      inst.addEventListener(
        'annotations.create',
        async function (createdAnnotations: List<AnnotationsUnion>) {
          const annotation = createdAnnotations.get(0);

          await handleAnnotationCreation(
            inst,
            annotation as AnnotationsUnion,
            mySignatureIdsRef,
            setSignatureAnnotationIds,
            currUser.email,
          );
        },
      );

      inst.addEventListener('annotations.update', async (event) => {
        const annotation = event.get(0) as AnnotationsUnion;

        // Update stored bounding box if this is the tracked radio button
        if (annotation?.id === lastDuplicatedRadioButtonBbox?.annotationId) {
          const updatedBbox = [
            annotation.boundingBox.left,
            annotation.boundingBox.top,
            annotation.boundingBox.width,
            annotation.boundingBox.height,
          ];
          setLastDuplicatedRadioButtonBbox({
            annotationId: annotation.id,
            bbox: updatedBbox,
          });
        }
      });

      inst.addEventListener(
        'annotations.delete',
        async (deletedAnnotations: any) => {
          const annotation = deletedAnnotations.get(0);

          await handleAnnotationDelete(inst, annotation);
          const updatedAnnotationIds = mySignatureIdsRef.current.filter(
            (id) => id !== annotation.id,
          );
          setSignatureAnnotationIds(updatedAnnotationIds);
          mySignatureIdsRef.current = updatedAnnotationIds;
        },
      );

      inst.addEventListener(
        'formFields.update',
        async (updatedFields: List<FormField>) => {
          const updatedField = updatedFields.get(0);
          await handleFormFieldUpdate(inst, updatedFields);
        },
      );

      inst.addEventListener(
        'formFieldValues.update',
        async (updatedValues: any) => {
          await handleFormFieldValueUpdate(inst, updatedValues);
        },
      );

      setIsTextAnnotationMovable(true);
    },
    [featureFlags, lastDuplicatedRadioButtonBbox],
  );

  useEffect(() => {
    if (nutrientInstance) {
      trackInst.current = nutrientInstance;
      configureInstanceEvents(nutrientInstance);
    }
  }, [configureInstanceEvents, nutrientInstance]);

  const cleanupPSPDFKit = async () => {
    if (nutrientInstance && viewer.current) {
      await NutrientViewer.unload(viewer.current);
      viewer.current = null;
      setNutrientInstance(null);
      setLastDuplicatedRadioButtonBbox(null);
    }
  };

  useEffect(() => {
    if (
      instantJSON &&
      instantJSON.customFromAPI &&
      viewer.current &&
      !isEmpty(instantJSON) &&
      renderCount === 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      loadPSPDFKit(instantJSON, viewer.current!);
      renderCount++;
    }
  }, [instantJSON, loadPSPDFKit]);

  useEffect(() => {
    if (checked) {
      userChange(currUser, NutrientViewer, nutrientInstance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  const debouncedHandlersCleanupRef = useRef<(() => void) | null>(null);

  useUnmount(async () => {
    if (debouncedHandlersCleanupRef.current) {
      debouncedHandlersCleanupRef.current();
    }
    await cleanupPSPDFKit();
    renderCount = 0;
  });

  const handleAssigneeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedAssignee = contacts.find(
      (contact) => contact.email === event.target.value,
    );
    setCurrSignee(selectedAssignee || defaultAssignee);
  };

  // Hack: Function to store duplicated radio button bounding box
  const storeDuplicatedRadioButtonBbox = (
    annotationId: string,
    bbox: number[],
  ) => {
    setLastDuplicatedRadioButtonBbox({ annotationId, bbox });
  };

  const onSave = async () => {
    if (!nutrientInstance) {
      console.error('[onSave] NutrientViewer instance not initialized');
      toast.show({
        text: 'PDF viewer not initialized. Please try again.',
        variant: 'error',
      });
      return;
    }

    await nutrientInstance.save();

    const instantJSONPayload = await nutrientInstance.exportInstantJSON();

    // Hack: Manually inject the correct bounding box for the last duplicated radio button
    if (lastDuplicatedRadioButtonBbox && instantJSONPayload.annotations) {
      const annotationIndex = instantJSONPayload.annotations.findIndex(
        (annotation: any) =>
          annotation.id === lastDuplicatedRadioButtonBbox.annotationId,
      );

      if (annotationIndex !== -1) {
        instantJSONPayload.annotations[annotationIndex].bbox =
          lastDuplicatedRadioButtonBbox.bbox as [
            number,
            number,
            number,
            number,
          ];

        // Clear the stored data after injection
        setLastDuplicatedRadioButtonBbox(null);
      }
    }

    if (!instantJSONPayload) {
      console.error('[onSave] Failed to export Instant JSON');
      toast.show({
        text: 'Failed to save document data. Please try again.',
        variant: 'error',
      });
      return;
    }

    const payloadInstantJSON = await generateInstantJSON(instantJSONPayload);

    const responseHandlers = {
      onSuccess: () => {
        toast.show({
          text: 'File data saved successfully',
          variant: 'success',
        });

        handleClose();
      },
      onError: (e: unknown) => {
        if ((e as AxiosError).status === 500) {
          toast.show({
            text: 'Internal Server Error',
            variant: 'error',
          });
        } else if ((e as AxiosError).status === 422) {
          toast.show({
            text: 'The selected type is invalid',
            variant: 'error',
          });
        }
      },
    };

    if (liveFormFieldId) {
      updateMutate(
        {
          fileId,
          liveFormFieldId,
          liveFormData: {
            xfdf_data: '',
            json_data: JSON.stringify(payloadInstantJSON),
            type: siStore.liveFormType,
            is_type_signature_enabled: isSignatureTypeEnabled,
          },
        },
        responseHandlers,
      );
    } else {
      saveMutate(
        {
          fileId,
          liveFormData: {
            xfdf_data: '',
            json_data: JSON.stringify(payloadInstantJSON),
            type: siStore.liveFormType,
            is_type_signature_enabled: isSignatureTypeEnabled,
          },
        },
        responseHandlers,
      );
    }
  };

  const applyAndSave = async () => {
    try {
      await onSave();
    } catch (e) {
      console.error(e);
      toast.show({
        text: 'Something went wrong! Reload the page and try again',
        variant: 'error',
      });
    }
  };

  const handleClose = async () => {
    await cleanupPSPDFKit();
    onClose();
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    onChangeReadyToSign(event.target.checked, user, NutrientViewer);
  };

  const onChangeReadyToSign = async (
    value: boolean,
    user: TUser | null,
    PSPDFKit: any,
  ) => {
    if (nutrientInstance) {
      if (value) {
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set('interactionMode', PSPDFKit.InteractionMode.PAN),
        );
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set('showToolbar', false),
        );
        setIsTextAnnotationMovable(false);
      } else {
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set(
            'interactionMode',
            PSPDFKit.InteractionMode.FORM_CREATOR,
          ),
        );
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set('showToolbar', true),
        );
        setIsTextAnnotationMovable(true);
      }
    }
  };

  const handleSignatureTypeChange = useCallback((enabled: boolean) => {
    setIsSignatureTypeEnabled(enabled);
    // State is updated, will be saved when the form is submitted
  }, []);

  return (
    <Dialog
      id="form-viewer-modal"
      fullScreen
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
    >
      <StyledHeader>
        <HeaderTitle>{fileName}</HeaderTitle>
        <StyledHeaderActions spacing={1} direction="row">
          <Button variant="outlined" color="tertiary" onClick={handleClose}>
            Back
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={applyAndSave}
            loading={saveLoading || updateLoading}
          >
            Save & close
          </LoadingButton>
        </StyledHeaderActions>
      </StyledHeader>
      <StyledWebViewerBox>
        <SidePanel
          users={contacts}
          currSignee={currSignee}
          onDragStart={(
            event: React.DragEvent<HTMLDivElement>,
            type: string,
            dateFormat?: string,
          ) =>
            onDragStart(
              event,
              type,
              currSignee,
              nutrientInstance,
              siStore.liveFormType,
              0,
              dateFormat,
            )
          }
          onDragEnd={onDragEnd}
          handleChange={handleAssigneeChange}
          type={siStore.liveFormType}
          checked={checked || loading}
          handleCheck={handleCheck}
          isSignatureTypeEnabled={isSignatureTypeEnabled}
          onSignatureTypeChange={handleSignatureTypeChange}
        />
        <StyledViewer ref={viewer} onDragOver={handleDragOver} />
      </StyledWebViewerBox>
    </Dialog>
  );
};
